import { addClassNamesToElement } from '@lexical/utils'
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey
} from 'lexical'

import { AttachmentCard } from '../../ui/AttachmentCard'
import { initialAttachmentCardState } from '../../ui/AttachmentCard/Store/store/createStore'
import { ATTACHMENT_CARD_SYNC_STATE_KEYS } from '../../ui/AttachmentCard/Synchorizner'
import { isEqualState } from '../../utils/state'

import {
  AttachmentNodeProps,
  AttachmentNodeState,
  SerializedAttachmentNode
} from './interface'

export class AttachmentNode extends DecoratorNode<React.JSX.Element> {
  private __file?: File
  private __data: AttachmentNodeState

  constructor({ file, state }: AttachmentNodeProps, key?: NodeKey) {
    super(key)
    this.__file = file
    this.__data = {
      ...initialAttachmentCardState,
      ...state
    }
  }

  getKey() {
    return this.__key
  }

  getFile() {
    return this.__file
  }

  getState() {
    return this.__data
  }

  setState(state: Partial<AttachmentNodeState>) {
    const writable = this.getWritable()
    writable.__data = {
      ...writable.__data,
      ...state
    }
  }

  static getType() {
    return 'attachment'
  }

  /**
   * 每次状态变更时都会调用，所以不能传 file 防止 AttachmentCard 里面重复上传
   */
  static clone(node: AttachmentNode) {
    return new AttachmentNode(
      {
        file: node.getFile(),
        state: node.getState()
      },
      node.getKey()
    )
  }

  exportJSON(): SerializedAttachmentNode {
    return {
      ...super.exportJSON(),
      state: this.getState(),
      type: AttachmentNode.getType()
    }
  }

  static importJSON(_serializedNode: SerializedAttachmentNode) {
    return $createAttachmentNode(_serializedNode)
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    return { element: editor.getElementByKey(this.getKey()) }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: () => ({
        priority: 0,
        conversion(dom) {
          if (!(dom instanceof HTMLDivElement)) {
            return null
          }
          const dataset = dom.dataset
          if (!dataset.ossId || !dataset.error) {
            return null
          }
          return { node: $createAttachmentNode({ state: dataset }) }
        }
      })
    }
  }

  createDOM(config: EditorConfig) {
    const div = document.createElement('div')
    addClassNamesToElement(div, config.theme.attachment)
    Object.assign(div.dataset, this.getState())
    return div
  }

  updateDOM(
    _prevNode: AttachmentNode,
    _dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    return isEqualState(
      this.getState(),
      _prevNode.getState(),
      ATTACHMENT_CARD_SYNC_STATE_KEYS
    )
  }

  isInline(): boolean {
    return true
  }

  isIsolated(): boolean {
    return false
  }

  decorate() {
    return (
      <AttachmentCard
        file={this.getFile()}
        nodeKey={this.getKey()}
        {...this.getState()}
      />
    )
  }
}

export const $isAttachmentNode = (
  node: LexicalNode | null
): node is AttachmentNode => {
  return node instanceof AttachmentNode
}

export const $createAttachmentNode = (props: AttachmentNodeProps) => {
  return $applyNodeReplacement(new AttachmentNode(props))
}
